
import React from 'react';
import { SignUpSuccessMessagePlain } from '../components/SignUpSuccessMessagePlain';
import AuthLayout from '../components/AuthLayout';
import SocialLogin from '../components/SocialLogin';
import axios from 'axios';
import { API_BASE_URL } from '../lib/constants';
import { redirectAfterLogin } from '../lib/authRedirect';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '../lib/usePageTitle';

export default function LoginPage() {
  // Show account-created message if redirected from sign-up
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const showAccountCreatedMsg = location.state && location.state.accountCreated;
  
  // Handle email verification messages
  const verification = searchParams.get('verification');
  const verificationMessage = searchParams.get('message');
  
  usePageTitle('Login');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API_BASE_URL}/api/current-user/`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(() => {
          navigate('/current-user');
        })
        .catch(() => {
          localStorage.removeItem('token');
        });
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // Use Django SimpleJWT endpoint for login
      const response = await axios.post(`${API_BASE_URL}/api/token/`, { username: email, password });
      // Store JWT access and refresh tokens in localStorage
      localStorage.setItem('token', response.data.access);
      if (response.data.refresh) {
        localStorage.setItem('refresh', response.data.refresh);
      }
      // Centralized redirect
      redirectAfterLogin();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Sign In" description="Access your account below.">
      {showAccountCreatedMsg && (
        <div style={{ marginBottom: 8 }}>
          <SignUpSuccessMessagePlain />
        </div>
      )}
      {verification && verificationMessage && (
        <div style={{ 
          marginBottom: 16, 
          padding: 12, 
          borderRadius: 6,
          backgroundColor: verification === 'success' ? '#d1f2eb' : '#fadbd8',
          color: verification === 'success' ? '#0e5132' : '#721c24',
          border: `1px solid ${verification === 'success' ? '#82e0aa' : '#e8a29b'}`
        }}>
          {decodeURIComponent(verificationMessage)}
        </div>
      )}
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          style={{ padding: 10, borderRadius: 6, border: '1px solid #ddd' }}
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
          style={{ padding: 10, borderRadius: 6, border: '1px solid #ddd' }}
        />
        {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
        <button type="submit" disabled={loading} style={{ padding: 10, borderRadius: 6, background: '#2d6cdf', color: '#fff', fontWeight: 600, border: 'none' }}>
          {loading ? 'Logging in...' : 'Login'}
        </button>
      </form>
      <SocialLogin onError={setError} />
    </AuthLayout>
  );
}
